import React, { useEffect, useState } from "react";
import { api, formatApiError } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { ShieldCheck, Upload, FileText, CheckCircle2, AlertCircle, Clock, Plus } from "lucide-react";
import { toast } from "sonner";

const CRED_TYPES = ["RN License", "BLS", "ACLS", "PALS", "NIHSS", "COVID Vaccine", "Flu Vaccine", "Background Check", "Drug Screen", "TB Test"];

export default function Credentials() {
  const { user } = useAuth();
  const isClinician = user?.role === "clinician";
  const [mine, setMine] = useState([]);
  const [queue, setQueue] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ type: "RN License", number: "", state: "TX", issue_date: "", expiration_date: "", document_name: "" });

  const load = async () => {
    try {
      if (isClinician) {
        const { data } = await api.get("/credentials/mine");
        setMine(data);
      } else {
        const { data } = await api.get("/credentials", { params: { status: "pending" } });
        setQueue(data);
      }
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [user]);

  const submitCred = async (e) => {
    e.preventDefault();
    try {
      await api.post("/credentials", form);
      toast.success("Credential uploaded");
      setShowForm(false);
      setForm({ type: "RN License", number: "", state: "TX", issue_date: "", expiration_date: "", document_name: "" });
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const review = async (id, action) => {
    try {
      await api.post(`/credentials/${id}/action`, { action });
      toast.success(`Credential ${action}d`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const StatusBadge = ({ s }) => {
    if (s === "approved") return <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-emerald-50 text-emerald-700 text-[11px] font-bold uppercase tracking-wider"><CheckCircle2 className="w-3 h-3" /> Approved</span>;
    if (s === "rejected") return <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-red-50 text-trital-red text-[11px] font-bold uppercase tracking-wider"><AlertCircle className="w-3 h-3" /> Rejected</span>;
    return <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-amber-50 text-amber-700 text-[11px] font-bold uppercase tracking-wider"><Clock className="w-3 h-3" /> Pending</span>;
  };

  return (
    <div className="space-y-6" data-testid="credentials-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="overline text-trital-red">Compliance</div>
          <h1 className="mt-1 font-display font-black text-3xl lg:text-4xl text-slate-900 tracking-tight">Credentials</h1>
          <p className="text-slate-600 text-sm">{isClinician ? "Upload and track your licenses & certifications." : "Review and approve submitted credentials."}</p>
        </div>
        {isClinician && (
          <button onClick={() => setShowForm((v) => !v)} className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-trital-navy text-white text-sm font-semibold btn-lift hover:bg-trital-navy-deep" data-testid="add-credential-btn">
            <Plus className="w-4 h-4" /> Add Credential
          </button>
        )}
      </div>

      {isClinician && showForm && (
        <form onSubmit={submitCred} className="rounded-2xl bg-white border border-slate-200 p-5 shadow-card grid md:grid-cols-3 gap-3" data-testid="credential-form">
          <select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} className="px-3 py-2 rounded-lg border border-slate-300 text-sm">
            {CRED_TYPES.map((t) => <option key={t}>{t}</option>)}
          </select>
          <input placeholder="Number / ID" value={form.number} onChange={(e) => setForm({ ...form, number: e.target.value })} className="px-3 py-2 rounded-lg border border-slate-300 text-sm" />
          <input placeholder="State" value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value })} className="px-3 py-2 rounded-lg border border-slate-300 text-sm" />
          <input type="date" value={form.issue_date} onChange={(e) => setForm({ ...form, issue_date: e.target.value })} className="px-3 py-2 rounded-lg border border-slate-300 text-sm" />
          <input type="date" value={form.expiration_date} onChange={(e) => setForm({ ...form, expiration_date: e.target.value })} className="px-3 py-2 rounded-lg border border-slate-300 text-sm" />
          <input placeholder="Document name (optional)" value={form.document_name} onChange={(e) => setForm({ ...form, document_name: e.target.value })} className="px-3 py-2 rounded-lg border border-slate-300 text-sm" />
          <button type="submit" className="md:col-span-3 px-4 py-2.5 rounded-full bg-trital-navy text-white font-semibold text-sm" data-testid="credential-submit">Submit for review</button>
        </form>
      )}

      {isClinician ? (
        <div className="grid md:grid-cols-2 gap-3">
          {mine.length === 0 && <div className="text-slate-500 bg-white border border-slate-200 rounded-xl p-6 md:col-span-2">You haven't uploaded any credentials yet.</div>}
          {mine.map((c) => (
            <div key={c.id} className="rounded-2xl bg-white border border-slate-200 p-5 shadow-card">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-trital-medical-blue-soft text-trital-navy flex items-center justify-center"><FileText className="w-5 h-5" /></div>
                  <div>
                    <div className="font-display font-bold text-slate-900">{c.type}</div>
                    <div className="text-xs text-slate-500">{c.state ? `${c.state} • ` : ""}{c.number || "—"}</div>
                  </div>
                </div>
                <StatusBadge s={c.status} />
              </div>
              {c.expiration_date && <div className="mt-3 text-xs text-slate-500">Expires: <span className="text-slate-800">{new Date(c.expiration_date).toLocaleDateString()}</span></div>}
              {c.review_note && <div className="mt-2 text-xs text-slate-600 italic">Reviewer note: {c.review_note}</div>}
            </div>
          ))}
        </div>
      ) : (
        <div className="grid gap-3">
          {queue.length === 0 && <div className="text-slate-500 bg-white border border-slate-200 rounded-xl p-6">No credentials pending review.</div>}
          {queue.map((c) => (
            <div key={c.id} className="rounded-2xl bg-white border border-slate-200 p-5 shadow-card flex items-center justify-between">
              <div>
                <div className="font-display font-bold text-slate-900">{c.type} — {c.user_name}</div>
                <div className="text-xs text-slate-500">{c.state} {c.number && `• ${c.number}`}</div>
              </div>
              <div className="flex gap-2">
                <button onClick={() => review(c.id, "approve")} className="px-4 py-2 rounded-full bg-trital-navy text-white text-sm font-semibold btn-lift" data-testid={`approve-${c.id}`}>Approve</button>
                <button onClick={() => review(c.id, "reject")} className="px-4 py-2 rounded-full border border-slate-300 text-slate-700 text-sm font-semibold hover:bg-slate-50">Reject</button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
